import { createSlice } from '@reduxjs/toolkit'
import tokenService from '../../services/tokenService'
import { baseApi } from '../api/baseApi'
import { workspaceReset } from './workspaceSlice'

// Persisted user profile — avoids the "Unknown User → UU" flash on
// every page refresh. Without this, `user` starts as null on boot,
// SidebarFooter renders "Unknown User" → initials "UU" for the
// ~200ms window until `fetchProfile` resolves and dispatches
// `profileSet`. Stored under a stable key, read synchronously in
// initialState, kept in sync via the reducers below.
const USER_STORAGE_KEY = 'auth.user'

function loadStoredUser() {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function storeUser(user) {
  try {
    if (user) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
    else localStorage.removeItem(USER_STORAGE_KEY)
  } catch {
    /* ignore storage errors (private browsing, quota, etc.) */
  }
}

const initialState = {
  user: loadStoredUser(),
  isAuthenticated: !!tokenService.getAccessToken(),
  status: 'idle', // idle | loading | error
  error: '',
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    requestStarted(state) {
      state.status = 'loading'
      state.error = ''
    },
    requestFailed(state, action) {
      state.status = 'error'
      state.error = action.payload || ''
    },
    authSucceeded(state) {
      state.isAuthenticated = true
      state.status = 'idle'
      state.error = ''
    },
    profileSet(state, action) {
      state.user = action.payload || null
      storeUser(state.user)
    },
    profileCleared(state) {
      state.user = null
      state.isAuthenticated = false
      storeUser(null)
    },
    loggedOut(state) {
      state.user = null
      state.isAuthenticated = false
      state.status = 'idle'
      state.error = ''
      storeUser(null)
    },
    forceLogout(state) {
      state.user = null
      state.isAuthenticated = false
      state.status = 'idle'
      state.error = ''
      storeUser(null)
    },
  },
})

export const {
  requestStarted,
  requestFailed,
  authSucceeded,
  profileSet,
  profileCleared,
  loggedOut,
  forceLogout,
} = authSlice.actions

export const selectUser = (state) => state.auth.user
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated
export const selectAuthStatus = (state) => state.auth.status
export const selectAuthError = (state) => state.auth.error

// Thunk: clear tokens from storage and reset every account-scoped
// piece of state. Use instead of dispatching loggedOut() directly so
// the side-effect (token clear + cache wipe) always travels with the
// state reset.
//
// Resets, in order:
//   1. tokenService — removes access/refresh tokens from localStorage.
//   2. baseApi cache — clears every RTK Query subscription/result so
//      Account A's recent-tasks, triggers, notifications, etc. don't
//      bleed into Account B's session. Without this, the sidebar
//      shows A's tasks until a hard refresh because each query
//      refetches lazily and stale cache data is rendered in the gap.
//   3. workspaceSlice — clears in-memory messages / brainEvents /
//      taskStatus / wsStage so a logged-in /task/{id} view doesn't
//      flash A's conversation when B navigates back to that URL.
//   4. authSlice — flips isAuthenticated, clears user, resets status.
//      Done last so consumers reading auth state see fresh on the
//      same dispatch tick the cache is empty.
export const logout = () => (dispatch) => {
  fetch('/claims-api/api/demo/logout', {method:'POST'}).catch(() => {});
  tokenService.clear()
  dispatch(baseApi.util.resetApiState())
  dispatch(workspaceReset())
  dispatch(loggedOut())
}

export default authSlice.reducer
